import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useSessionStorage } from 'usehooks-ts';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { DEVICE_STORAGE_KEY } from '@/utils/constants';
import { EmbedLayout } from '@/utils/embed-layout';
import { timeoutPromise } from '@/utils/promise';

type State = {
  hydrated: boolean;
  touchStart: boolean;
  fcmToken: string;
  embedLayout: EmbedLayout;
  alwaysEnterAsSubmit: boolean;
  isPrivate: boolean | undefined;
  isNotificationPermissionRequested: boolean;
  compatibilityCheckedAt: number;
  requireRestart: boolean;
  enterAsSubmit: boolean | null;
};

type Action = {
  setHydrated(): void;
  setEnterAsSubmit(value: boolean): void;
  clear(): void;
};

type DeviceStore = State & Action;

export const useDeviceStore = create<DeviceStore>()(
  persist(
    (set): DeviceStore => ({
      hydrated: false,
      touchStart: false,
      fcmToken: '',
      alwaysEnterAsSubmit: false,
      embedLayout: EmbedLayout.FULL,
      isPrivate: undefined,
      isNotificationPermissionRequested: false,
      compatibilityCheckedAt: 0,
      requireRestart: false,
      enterAsSubmit: null,
      setHydrated() {
        set({ hydrated: true });
      },
      setEnterAsSubmit: (value) => set({ enterAsSubmit: value }),
      clear() {
        set({ fcmToken: '' });
      },
    }),
    {
      name: DEVICE_STORAGE_KEY,
      version: 6,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        fcmToken: state.fcmToken,
        isNotificationPermissionRequested: state.isNotificationPermissionRequested,
        compatibilityCheckedAt: state.compatibilityCheckedAt,
        enterAsSubmit: state.enterAsSubmit,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
      migrate: () => ({
        fcmToken: '',
        isNotificationPermissionRequested: false,
        compatibilityCheckedAt: 0,
        enterAsSubmit: null,
      }),
    },
  ),
);

export const setFcmDeviceToken = (token: string) => useDeviceStore.setState(() => ({ fcmToken: token }));
export const requestRestart = (value = true) => useDeviceStore.setState(() => ({ requireRestart: value }));
export const setIsPrivate = (isPrivate: boolean) => useDeviceStore.setState(() => ({ isPrivate }));
export const setNotificationPermissionRequested = (toggle: true) =>
  useDeviceStore.setState(() => ({ isNotificationPermissionRequested: toggle }));
export const setCompatibilityChecked = () => useDeviceStore.setState(() => ({ compatibilityCheckedAt: Date.now() }));

export const setEmbedLayout = (input: EmbedLayout | number | string | null | undefined) => {
  let embedLayout: EmbedLayout;
  switch (input) {
    case 'mini':
    case 0:
    case EmbedLayout.MINI:
      embedLayout = EmbedLayout.MINI;
      break;
    case 'compact':
    case 1:
    case EmbedLayout.COMPACT:
      embedLayout = EmbedLayout.COMPACT;
      break;
    default:
      embedLayout = EmbedLayout.FULL;
      break;
  }

  useDeviceStore.setState(() => ({ embedLayout }));
};

export const useEmbedLayout = () => useDeviceStore((state) => state.embedLayout);

export const setAlwaysEnterAsSubmit = (value: string | undefined | null | boolean) => {
  if (value === 'true' || value === true) {
    useDeviceStore.setState(() => ({ alwaysEnterAsSubmit: true }));
  } else {
    useDeviceStore.setState(() => ({ alwaysEnterAsSubmit: false }));
  }
};

export const useAlwaysEnterAsSubmit = () => useDeviceStore((state) => state.alwaysEnterAsSubmit);

export const getCompatibilityChecked = () =>
  timeoutPromise(
    () =>
      new Promise<boolean>((resolve) => {
        if (useDeviceStore.getState().hydrated) {
          const { compatibilityCheckedAt } = useDeviceStore.getState();
          const isChecked = compatibilityCheckedAt !== 0 && compatibilityCheckedAt < Date.now() + 86_400_000;
          resolve(isChecked);
        } else {
          const unsubscribe = useDeviceStore.subscribe(({ hydrated, compatibilityCheckedAt }) => {
            if (hydrated) {
              const isChecked = compatibilityCheckedAt !== 0 && compatibilityCheckedAt < Date.now() + 86_400_000;
              resolve(isChecked);
              unsubscribe();
            }
          });
        }
      }),
    1000,
    () => false,
  );

export function useDebugMode() {
  const [searchParams] = useSearchParams();
  const [debugMode, setDebugMode] = useSessionStorage('debug-mode', false);

  useEffect(() => {
    if (!searchParams.has('debug')) return;

    if (searchParams.get('debug') === 'off') {
      setDebugMode(false);
    } else {
      setDebugMode(true);
    }
  }, [searchParams, setDebugMode]);

  useEffect(() => {
    if (debugMode && 'VConsole' in window) {
      // @ts-ignore vConsole is imported via script tag in index.html
      const vConsole = new window.VConsole();

      return () => {
        vConsole.destroy();
      };
    }
  }, [debugMode]);

  return [debugMode, setDebugMode] as const;
}
